import { Injectable } from '@angular/core';
import { Hit } from '@faro/searchapi-angular-client';
import { TableHitComponentModel } from './table-hitlist/table-hit-component.model';
import { accessibilityDurationString } from '../../../shared/accessibility-duration.helper';
import { fromMilliseconds } from '../../../shared/duration';
import { SearchFieldSelectionEnum, SearchFieldSelectionOption } from '../../search-state/search-options.state';
import { RightsRestrictions } from 'src/app/shared/components/restriction-tooltip/restriction-tooltip.model';
import { formatTimeSpan } from 'src/app/shared/timespan';

@Injectable({
    providedIn: 'root',
})
export class HitMapperService {
    constructor() { }

    mapHitToTableHitComponentModel = (
        hit: Hit,
        selectedSearchOption: SearchFieldSelectionOption
    ): TableHitComponentModel => {
        const countryAndLocation = selectedSearchOption.value === SearchFieldSelectionEnum.IMAGE ? getCountryAndLocationForShots(hit) : '';
        return {
            hitId: hit.id,
            programId: this.programId(hit, selectedSearchOption),
            itemId: getItemId(hit, selectedSearchOption)?.toLowerCase(),
            sequenceId: getSequenceId(hit, selectedSearchOption)?.toLowerCase(),
            ccId: getCcId(hit, selectedSearchOption)?.toLowerCase(),
            mcId: getMcId(hit, selectedSearchOption)?.toLowerCase(),
            vtcIn: getVtcIn(hit, selectedSearchOption),
            vtcOut: getVtcOut(hit, selectedSearchOption),
            title: getTitle(hit, selectedSearchOption),
            date: getDate(hit, selectedSearchOption),
            duration: getDuration(hit, selectedSearchOption),
            durationAccessibilityString: this.getAccessibilityDurationString(getDuration(hit, selectedSearchOption)),
            // For TOPIC hits the description is rendered inside a host span of the cell (next to an
            // offscreen span fed by the composed "Thema" tooltip), so it carries no markup of its own.
            // For IMAGE/WORD hits it is rendered directly as cell content including the offscreen
            // screen-reader copy of the cellTip.
            description: selectedSearchOption.value === SearchFieldSelectionEnum.TOPIC
                ? `${getDescription(hit, selectedSearchOption)}`
                : `<span>${isNotEmpty(countryAndLocation) ? countryAndLocation + ': ' : ''}${getDescription(
                    hit,
                    selectedSearchOption
                )}</span><span class="h-offscreen">${getDescriptionCellTip(hit, selectedSearchOption)}</span>`,
            descriptionCellTip: getDescriptionCellTip(hit, selectedSearchOption),
            itemTitle: `<a class="accessibility-link" href="details/${this.programId(hit, selectedSearchOption)}/${getItemId(hit, selectedSearchOption)
                }"> ${getItemTitle(hit, selectedSearchOption)}</a><span class="h-offscreen">${getItemTitleCellTip(
                    hit,
                    selectedSearchOption
                )}</span>`,
            itemTitleCellTip: getItemTitleCellTip(hit, selectedSearchOption),
            source: getSource(hit, selectedSearchOption),
            keyframeId: getKeyframeIndex(hit, selectedSearchOption),
            restrictions: getRestrictions(hit, selectedSearchOption),
            ratings: getRatings(hit, selectedSearchOption),
            hasNonOtonRatings: Boolean(getRatings(hit, selectedSearchOption)) && getRatings(hit, selectedSearchOption) !== 'O-Ton',
            speaker: getSpeaker(hit, selectedSearchOption),
            validated: getValid(hit, selectedSearchOption) === 'False' ? 'nicht validiert' : undefined,
            context: getContext(hit, selectedSearchOption),
        };
    };

    programId(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
        return getProgramId(hit, selectedSearchOption)?.toLowerCase();
    }

    itemId(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
        return getItemId(hit, selectedSearchOption)?.toLowerCase();
    }

    ccId(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
        return getCcId(hit, selectedSearchOption)?.toLowerCase();
    }
    
    mcId(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
        return getMcId(hit, selectedSearchOption)?.toLowerCase();
    }

    getAccessibilityDurationString(duration: string | undefined): string {
        const parsedDuration = fromMilliseconds(parseInt(duration ?? '0'));
        return accessibilityDurationString(parsedDuration);
    }
}

enum ItemHitKeys {
    NUMBER = 'Item.Hitlist.ItemNr',
    PROGRAM_TITLE = 'Item.Hitlist.PresTitle',
    ITEM_TITLE = 'Item.Hitlist.PresentationBTIT',
    DESCRIPTION = 'Item.Hitlist.PresentationABS',
    DATE = 'Item.Hitlist.VirtDateStart',
    DURATION = 'Item.Hitlist.Length',
    KEYFRAME = 'Item.Hitlist.KF',
    SOURCE = 'Item.Hitlist.PresRight',
    PROGRAM_ID = 'Item.Hitlist.ProgramId',
    USAGE_CONSTRAINT_REMARK = "Item.Hitlist.UsageConstraintRemark",
    USAGE_CONSTRAINTS = "Item.Hitlist.UsageConstraints",
    RIGHTS = "Item.Hitlist.Rights",
}

enum SequenceHitKeys {
    NUMBER = 'Sequence.Hitlist.ItemNr',
    PROGRAM_TITLE = 'Sequence.Hitlist.PresTitle',
    ITEM_TITLE = 'Sequence.Hitlist.PresentationBTIT',
    DESCRIPTION = 'Sequence.Hitlist.PresentationABS',
    DATE = 'Sequence.Hitlist.VirtDateStart',
    DURATION = 'Sequence.Hitlist.Length',
    KEYFRAME = 'Sequence.Hitlist.KF',
    SOURCE = 'Sequence.Hitlist.PresRight',
    PROGRAM_ID = 'Sequence.Hitlist.ProgramId',
    ITEM_ID = 'Sequence.Hitlist.ItemId',
    LOCATION = 'Sequence.Hitlist.Location',
    COUNTRIES = 'Sequence.Hitlist.Countries',
    USAGE_LIMITATION = "Sequence.Hitlist.UsageLimitation",
    USAGE_CONSTRAINTS = "Sequence.Hitlist.UsageConstraints",
    RIGHTS = "Sequence.Hitlist.Rights",
    RATINGS = "Sequence.Hitlist.Rating"
}

enum ClosedCaptionHitKeys {
    PROGRAM_TITLE = 'ClosedCaption.Hitlist.PresTitle',
    ITEM_TITLE = 'ClosedCaption.Hitlist.PresentationBTIT',
    DESCRIPTION = 'ClosedCaption.Hitlist.Text',
    DATE = 'ClosedCaption.Hitlist.VirtDateStart',
    DURATION = 'ClosedCaption.Hitlist.Length',
    KEYFRAME = 'ClosedCaption.Hitlist.KF',
    PROGRAM_ID = 'ClosedCaption.Hitlist.PID',
    ITEM_ID = 'ClosedCaption.Hitlist.IID',
    SEQUENCE_ID = 'ClosedCaption.Hitlist.SID',
    MEDIA_CUT_ID = "ClosedCaption.Hitlist.MCID",
    CLOSED_CAPTION_ID = 'ClosedCaption.Hitlist.WS.RefID',
    VTC_IN = 'ClosedCaption.Hitlist.VTC_IN',
    //AVCOMPONENT_TYPE = 'ClosedCaption.Hitlist.Type',

    RATINGS = "ClosedCaption.Hitlist.WS.Rate",
    SPEAKER = "ClosedCaption.Hitlist.WS.Person",
    VALID = "ClosedCaption.Hitlist.WS.Valid",
    CONTEXT = "ClosedCaption.Hitlist.WS.Context",
}

function getHitPropertyValue(hit: Hit, keys: [ItemHitKeys | SequenceHitKeys | ClosedCaptionHitKeys], isCellTip: boolean): string {
    let stringValue = isCellTip
        ? (hit.items?.[keys[0]]?.cellTip ?? '')
        : (hit.items?.[keys[0]]?.value ?? '')
    return stringValue;
}

function getHitPropertyArrayValue(hit: Hit, keys: [ItemHitKeys | SequenceHitKeys | ClosedCaptionHitKeys], isCellTip: boolean): string[] {
    let stringValue = getHitPropertyValue(hit, keys, isCellTip);
    return isNotEmpty(stringValue) ? stringValue.replaceAll("&#166;", "¦").split('¦') : [];
}

function getTitle(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.PROGRAM_TITLE,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.PROGRAM_TITLE,
        [SearchFieldSelectionEnum.WORD]: ClosedCaptionHitKeys.PROGRAM_TITLE,
    };

    const key = keyMap[selectedSearchOption.value];
    return key ? getHitPropertyValue(hit, [key], false) : undefined;
}

function getItemTitle(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.ITEM_TITLE,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.ITEM_TITLE,
        [SearchFieldSelectionEnum.WORD]: ClosedCaptionHitKeys.ITEM_TITLE,
    };

    const key = keyMap[selectedSearchOption.value];
    return key ? getHitPropertyValue(hit, [key], false) : undefined;
}

function getItemTitleCellTip(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.ITEM_TITLE,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.ITEM_TITLE,
        [SearchFieldSelectionEnum.WORD]: ClosedCaptionHitKeys.ITEM_TITLE,
    };

    const key = keyMap[selectedSearchOption.value];
    return key ? getHitPropertyValue(hit, [key], true) : undefined;
}

function getDate(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.DATE,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.DATE,
        [SearchFieldSelectionEnum.WORD]: ClosedCaptionHitKeys.DATE,
    };

    const key = keyMap[selectedSearchOption.value];
    return key ? getHitPropertyValue(hit, [key], false) : undefined;
}

function getDuration(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.DURATION,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.DURATION,
        [SearchFieldSelectionEnum.WORD]: ClosedCaptionHitKeys.DURATION,
    };

    const key = keyMap[selectedSearchOption.value];
    return key ? getHitPropertyValue(hit, [key], false) : undefined;
}

function getDescription(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.DESCRIPTION,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.DESCRIPTION,
        [SearchFieldSelectionEnum.WORD]: ClosedCaptionHitKeys.DESCRIPTION,
    };

    const key = keyMap[selectedSearchOption.value];
    return key ? getHitPropertyValue(hit, [key], false) : undefined;
}

function getDescriptionCellTip(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.DESCRIPTION,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.DESCRIPTION,
        [SearchFieldSelectionEnum.WORD]: ClosedCaptionHitKeys.DESCRIPTION,
    };

    const key = keyMap[selectedSearchOption.value];
    return key ? getHitPropertyValue(hit, [key], true) : undefined;
}

function getSource(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.SOURCE,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.SOURCE,
        [SearchFieldSelectionEnum.WORD]: undefined,
    };

    const key = keyMap[selectedSearchOption.value];
    return key ? getHitPropertyValue(hit, [key], false) : undefined;
}

function getCountryAndLocationForShots(hit: Hit) {
    return [...getCountriesForShots(hit), getLocationForShots(hit)].filter(x => isNotEmpty(x)).join(", ");
}

function getLocationForShots(hit: Hit): string {
    return getHitPropertyValue(hit, [SequenceHitKeys.LOCATION], false) ?? '';
}

function getCountriesForShots(hit: Hit): string[] {
    let countries = getHitPropertyArrayValue(hit, [SequenceHitKeys.COUNTRIES], false) ?? [];
    return countries;
}

function getProgramId(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.PROGRAM_ID,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.PROGRAM_ID,
        [SearchFieldSelectionEnum.WORD]: ClosedCaptionHitKeys.PROGRAM_ID,
    };

    const key = keyMap[selectedSearchOption.value];
    return key ? getHitPropertyValue(hit, [key], false) : undefined;
}

function getItemId(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    if (selectedSearchOption.value === SearchFieldSelectionEnum.TOPIC) {
        return hit.id;
    }

    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: undefined,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.ITEM_ID,
        [SearchFieldSelectionEnum.WORD]: ClosedCaptionHitKeys.ITEM_ID,
    };

    const key = keyMap[selectedSearchOption.value];
    const itemId = key ? getHitPropertyValue(hit, [key], false) : undefined;
    return itemId && itemId !== '00000000-0000-0000-0000-000000000000' ? itemId : undefined;
}

function getSequenceId(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    if (selectedSearchOption.value === SearchFieldSelectionEnum.IMAGE) {
        return hit.id;
    }

    if (selectedSearchOption.value === SearchFieldSelectionEnum.WORD) {
        const seqId = getHitPropertyValue(hit, [ClosedCaptionHitKeys.SEQUENCE_ID], false);
        return seqId && seqId !== '00000000-0000-0000-0000-000000000000' ? seqId : undefined;
    }
    // For TOPIC, we don't have a sequence ID
    return undefined;
}

function getCcId(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    if (selectedSearchOption.value === SearchFieldSelectionEnum.WORD) {
        return getHitPropertyValue(hit, [ClosedCaptionHitKeys.CLOSED_CAPTION_ID], false);
    }
    return undefined;
}

function getMcId(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    if (selectedSearchOption.value === SearchFieldSelectionEnum.WORD) {
        return getHitPropertyValue(hit, [ClosedCaptionHitKeys.MEDIA_CUT_ID], false);
    }
    return undefined;
}

function getVtcIn(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    if (selectedSearchOption.value === SearchFieldSelectionEnum.WORD) {
        let vtcIn = getHitPropertyValue(hit, [ClosedCaptionHitKeys.VTC_IN], false);
        let vtcInAsMs = vtcIn ? parseInt(vtcIn) : undefined;
        return vtcInAsMs !== undefined && !isNaN(vtcInAsMs) 
            ? formatTimeSpan(fromMilliseconds(vtcInAsMs))
            : undefined;
    }
    return undefined;
}

function getVtcOut(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    if (selectedSearchOption.value === SearchFieldSelectionEnum.WORD) {
        let vtcIn = getHitPropertyValue(hit, [ClosedCaptionHitKeys.VTC_IN], false);
        let vtcInAsMs = vtcIn ? parseInt(vtcIn) : undefined;
        let duration = getDuration(hit, selectedSearchOption)
        let durationAsMs = duration ? parseInt(duration) : undefined;
        return vtcInAsMs !== undefined && !isNaN(vtcInAsMs) 
            && durationAsMs !== undefined && !isNaN(durationAsMs) 
            ? formatTimeSpan(fromMilliseconds(vtcInAsMs + durationAsMs))
            : undefined;
    }
    return undefined;
}

function getKeyframeIndex(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): number {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.KEYFRAME,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.KEYFRAME,
        [SearchFieldSelectionEnum.WORD]: ClosedCaptionHitKeys.KEYFRAME,
    };

    const key = keyMap[selectedSearchOption.value];
    return key ? Number.parseInt(getHitPropertyValue(hit, [key], false)) : NaN;
}

function getRestrictions(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): RightsRestrictions {
    let rights = getRights(hit, selectedSearchOption);
    let licenseHolder = getLicenseHolder(hit, selectedSearchOption);
    let usageConstraints = getUsageConstraints(hit, selectedSearchOption);
    let usageConstraintRemark = getUsageConstraintRemark(hit, selectedSearchOption);
    return {
        rights, licenseHolder, usageConstraints, usageConstraintRemark
    }
}

function getRights(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.RIGHTS,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.RIGHTS,
        [SearchFieldSelectionEnum.WORD]: undefined,
    };

    const key = keyMap[selectedSearchOption.value];
    const rights = key ? getHitPropertyArrayValue(hit, [key], false) : [];

    return rights.join("; ");
}

function getLicenseHolder(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.SOURCE,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.SOURCE,
        [SearchFieldSelectionEnum.WORD]: undefined,
    };

    const key = keyMap[selectedSearchOption.value];
    return key ? getHitPropertyValue(hit, [key], false) : undefined;
}

function getUsageConstraints(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.USAGE_CONSTRAINTS,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.USAGE_CONSTRAINTS,
        [SearchFieldSelectionEnum.WORD]: undefined,
    };

    const key = keyMap[selectedSearchOption.value];
    const usageConstraints = key ? getHitPropertyArrayValue(hit, [key], false) : [];

    return usageConstraints.join("; ");
}

function getUsageConstraintRemark(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: ItemHitKeys.USAGE_CONSTRAINT_REMARK,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.USAGE_LIMITATION,
        [SearchFieldSelectionEnum.WORD]: undefined,
    };

    const key = keyMap[selectedSearchOption.value];
    return key ? getHitPropertyValue(hit, [key], false) : undefined;
}

function getRatings(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    const keyMap = {
        [SearchFieldSelectionEnum.TOPIC]: undefined,
        [SearchFieldSelectionEnum.IMAGE]: SequenceHitKeys.RATINGS,
        [SearchFieldSelectionEnum.WORD]: ClosedCaptionHitKeys.RATINGS,
    };

    const key = keyMap[selectedSearchOption.value];
    const ratings = key ? getHitPropertyArrayValue(hit, [key], false) : [];

    return ratings.join("; ");
}

function getSpeaker(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    return selectedSearchOption.value === SearchFieldSelectionEnum.WORD ?
        getHitPropertyValue(hit, [ClosedCaptionHitKeys.SPEAKER], false) :
        undefined;
}

function getValid(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    return selectedSearchOption.value === SearchFieldSelectionEnum.WORD ?
        getHitPropertyValue(hit, [ClosedCaptionHitKeys.VALID], false) :
        undefined;
}

function getContext(hit: Hit, selectedSearchOption: SearchFieldSelectionOption): string | undefined {
    return selectedSearchOption.value === SearchFieldSelectionEnum.WORD ?
        getHitPropertyValue(hit, [ClosedCaptionHitKeys.CONTEXT], false) :
        undefined;
}

function isNotEmpty(value: string | undefined | null) {
    return value !== undefined && value !== null && value !== '';
}